import { forwardRef, type HTMLProps, type LegacyRef, useState } from 'react';
import type { DateRange } from 'rsuite/esm/DateRangePicker';
import assert from 'assert';
import type { ApiProject } from '@highimpact-frontend/api-types';
import { useAppSelector } from '../../store/hooks';
import { isDataState } from '../../store/data/state';
import StateDecorator from '../utils/state/StateDecorator';
import ProjectService from '../../api/services/config/project/ProjectService';
import UserProjectService from '../../api/services/config/user_project/UserProjectService';
import { createValidator, useForm } from '../../utils/hooks/useForm';
import { resetExternalState } from '../../store/data/slice';
import { useAppDispatch } from '../../store/hooks';
import CustomizedDialog from '../utils/MUISpecifics/dialog/CustumizedDialog';
import { Button, Stack } from '@mui/material';
import TextInput from '../utils/MUISpecifics/inputText/TextInput';
import DateTimeRangePickerComponent from '../utils/MUISpecifics/DateTimeRangePicker';
import SaveIcon from '@mui/icons-material/Save';
import { useSubmit } from '../../utils/hooks/useSubmit';
import { useTranslation } from 'react-i18next';
import DropdownItemsSelector from '../utils/MUISpecifics/dropDownItemSelector/DropDownItemSelector';
import DropDownMultiAutocompleteComponent from '../utils/MUISpecifics/dropDownMultiSelector/dropDownAutocompleteMultiSelector';

// Component props.
export interface FormProps extends HTMLProps<HTMLInputElement> {
  title?: string;
  onCancel: () => void;
  open: boolean;
}

const defaultValues: ApiProject = {
  name: '',
  is_admin: false,
};

// Component.
function CreateProjectForm(
  { open: isModalOpen, title, children, ...props }: FormProps,
  ref: LegacyRef<HTMLDivElement> | undefined
) {
  // use translation here :
  const { t } = useTranslation('components.projects');
  const [projectAdministratorId, setProjectAdministratorId] = useState<
    number | undefined
  >(undefined);
  const [selectedUsers, setSelectedUsers] = useState<
    Array<{ id: number; name: string }>
  >([]);
  const dispatch = useAppDispatch();

  const users = useAppSelector((state) => state.data_slice.externals.users);

  const { submit, state } = useSubmit();
  // Handle form submission.
  const onSubmit = async (data: ApiProject) => {
    submit({
      callbacks: [
        async () => {
          const project = await ProjectService.add(data);
          if (projectAdministratorId !== undefined) {
            await UserProjectService.add({
              user_id: projectAdministratorId,
              project_id: project.id,
              is_admin: true,
            });
          }
          await Promise.all(
            selectedUsers
              .filter((user) => user.id !== projectAdministratorId)
              .map((user) =>
                UserProjectService.add({
                  user_id: user.id,
                  project_id: project.id,
                  is_admin: false,
                })
              )
          );
        },
        () => props.onCancel(),
        () => dispatch(resetExternalState({ external_key: 'projects' })),
      ],
      errorMessages: {
        anyError: t('Une erreur est survenue lors de la création du projet'),
        unicityError: t('Un projet avec le même nom existe déjà'),
      },
      successMessage: t('Le projet a été créé avec succès'),
    });
  };

  /**
   * Create validation rule for each field.
   */
  const validator = createValidator<ApiProject>({
    name: [
      (n) => !n || n.trim().length <= 3
        ? t('Entrer au moins 4 caractères')
        : undefined,
    ],
    start_date: [],
    end_date: [],
    is_admin: []
  });

  // Define form hook.
  const methods = useForm<ApiProject>(defaultValues, validator, onSubmit);

  /**
   * Update the start and end dates.
   */
  const handleDateUpdate = (interval: DateRange | null) => {
    if (!interval) {
      methods.setMultipleValues({
        start_date: null,
        end_date: null,
      });
      return;
    }

    const [start, end] = interval;
    start.setTime(start.getTime() - start.getTimezoneOffset() * 60 * 1000);
    end.setTime(end.getTime() - end.getTimezoneOffset() * 60 * 1000);

    // To avoid strange behaviors with value state hook, setValues should not be used repeatedly.
    methods.setMultipleValues({
      start_date: start.toISOString(),
      end_date: end.toISOString(),
    });
  };

  // Update the name input.
  const handleName = (value: string) => {
    methods.setValue('name', value);
  };

  return StateDecorator([users], ref!, () => {
    assert(isDataState(users));

    return (
      <CustomizedDialog
        title={title}
        open={isModalOpen}
        onCancel={() => {
          props.onCancel();
          methods.reset();
        }}
      >
        <form
          onSubmit={methods.onSubmit}
          className={`form ${props.className || ''}`}
        >
          <Stack spacing={2}>
            <TextInput
              onChange={handleName}
              label={t('Nom du projet')}
              name={'name'}
              placeholder={t('Entrer le nom du projet')}
              errors={
                methods.hasSubmitted
                  ? { tag: 'Submited', errors: methods.getErrors('name') }
                  : { tag: 'Not submitted' }
              }
              required
            />

            <DateTimeRangePickerComponent
              label={t('Période du projet')}
              required={false}
              format="DD/MM/YYYY"
              onChange={handleDateUpdate}
              error={
                methods.getErrors('start_date').length > 0 ||
                methods.getErrors('end_date').length > 0
              }
              helperText={
                methods.getErrors('start_date')[0] ||
                methods.getErrors('end_date')[0] ||
                ''
              }
              safeDefault={false}
            />
            <DropdownItemsSelector
              onSelect={(selectedId) => {
                const administratorId = selectedId ?? undefined;
                setProjectAdministratorId(administratorId);
                setSelectedUsers((currentUsers) =>
                  currentUsers.filter((user) => user.id !== administratorId)
                );
              }}
              label={t('Administrateur du projet')}
              values={Object.values(users.data).map(user => ({ id: user.id, name: user.name + " " + user.surname }))}
              disabled={false}
              name="project_administrator_id"
              required={false}
            />
            <DropDownMultiAutocompleteComponent
              label={t('Utilisateurs associés au projet')}
              options={Object.values(users.data)
                .filter((user) => user.id !== projectAdministratorId)
                .map((user) => ({ id: user.id, name: user.name + " " + user.surname }))}
              value={selectedUsers}
              onChange={setSelectedUsers}
            />
            <Button
              type="submit"
              className="form-button-submit"
              variant="outlined"
              loadingPosition="end"
              data-testid="submit-project-button"
              endIcon={<SaveIcon />}
            >
              {state === 'Loading' ? t('Envoi...') : t('Créer')}
            </Button>
          </Stack>
        </form>
      </CustomizedDialog>
    );
  });
}

export default forwardRef(CreateProjectForm);
